import React, { useEffect, useState } from 'react';
import { Copy, Check, Share2, Stamp, ArrowLeft, MessageCircle, Ticket, Gift, UserCheck, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatTL } from '@/lib/format';
import { Card, StatusBadge, SectionTitle, Empty } from '@/components/shared';

export default function Invite() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);
  const [copied, setCopied] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [applying, setApplying] = useState(false);

  const load = () => {
    api.get('/architect/referrals').then((r) => setData(r.data)).catch(() => {});
    api.get('/architect/referral-leaderboard').then((r) => setBoard(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const copyCode = () => {
    if (!data) return;
    navigator.clipboard?.writeText(data.inviteCode);
    setCopied(true);
    toast.success('Davet kodu kopyalandı');
    setTimeout(() => setCopied(false), 1500);
  };

  const inviteUrl = () => `${window.location.origin}/?ref=${data.inviteCode}`;

  const shareText = () =>
    `Bella Maison Mimar Partner'a davetlisin! Davet kodum: ${data.inviteCode}\nHemen kaydol: ${inviteUrl()}`;

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText())}`, '_blank');
  };

  const share = async () => {
    if (!data) return;
    if (navigator.share) {
      try { await navigator.share({ title: 'Bella Maison Mimar Partner', text: shareText(), url: inviteUrl() }); } catch {}
    } else {
      navigator.clipboard?.writeText(shareText());
      toast.success('Davet metni kopyalandı');
    }
  };

  const copyLink = () => {
    if (!data) return;
    navigator.clipboard?.writeText(inviteUrl());
    toast.success('Davet bağlantısı kopyalandı');
  };

  const applyCode = async () => {
    if (!codeInput.trim()) return;
    setApplying(true);
    try {
      const r = await api.post('/architect/apply-invite', { code: codeInput.trim().toUpperCase() });
      toast.success(`${r.data.referrerName} tarafından davet edildin`);
      setCodeInput('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Davet kodu uygulanamadı');
    } finally { setApplying(false); }
  };

  if (!data) return <div className="p-6 text-navy/40 text-sm">Yükleniyor...</div>;

  const stampsInCycle = data.clubStamps;

  return (
    <div className="px-5 py-6 fade-up" data-testid="invite-page">
      <button onClick={() => navigate('/app')} className="flex items-center gap-1.5 text-navy/50 text-sm mb-4" data-testid="invite-back">
        <ArrowLeft strokeWidth={1.25} className="w-4 h-4" /> Geri
      </button>
      <h1 className="font-serif text-2xl text-navy mb-1">Davet</h1>
      <p className="text-navy/50 text-sm mb-5">
        {data.maxInvites} meslektaşını davet et; davet ettiğin mimarların ilk {data.referralSalesCount} satışının %{(data.referralRate * 100).toFixed(0)}'i sana prim olarak yansır.
      </p>

      {/* Invite code */}
      <Card className="p-6 mb-4 text-center">
        <p className="text-navy/50 text-xs mb-2">Davet kodun</p>
        <p className="font-serif text-3xl tracking-widest text-gold mb-3" data-testid="invite-code">{data.inviteCode}</p>
        <button onClick={copyLink} data-testid="invite-link-text" className="text-xs text-navy/50 hover:text-navy break-all mb-4 block w-full">
          {inviteUrl()}
        </button>
        <div className="grid grid-cols-3 gap-2.5">
          <button onClick={copyCode} data-testid="invite-copy-btn" className="flex flex-col items-center gap-1.5 border border-navy/15 rounded-md py-3 text-xs text-navy active:scale-95 transition-transform">
            {copied ? <Check strokeWidth={1.25} className="w-4 h-4 text-greenx" /> : <Copy strokeWidth={1.25} className="w-4 h-4" />}
            Kopyala
          </button>
          <button onClick={shareWhatsApp} data-testid="invite-whatsapp-btn" className="flex flex-col items-center gap-1.5 rounded-md py-3 text-xs text-cream bg-greenx active:scale-95 transition-transform">
            <MessageCircle strokeWidth={1.25} className="w-4 h-4" /> WhatsApp
          </button>
          <button onClick={share} data-testid="invite-share-btn" className="flex flex-col items-center gap-1.5 bg-navy text-cream rounded-md py-3 text-xs active:scale-95 transition-transform">
            <Share2 strokeWidth={1.25} className="w-4 h-4" /> Bağlantı
          </button>
        </div>
      </Card>

      {/* Tiered bonus rate */}
      <Card className="p-4 mb-4" data-testid="referral-tier-info">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <TrendingUp strokeWidth={1.25} className="w-5 h-5 text-gold" />
            <div>
              <p className="text-sm font-medium text-navy">Davet primin</p>
              <p className="text-xs text-navy/50">{data.referralCount} aktif davet</p>
            </div>
          </div>
          <span className="font-serif text-2xl text-gold" data-testid="referral-current-rate">%{(data.currentBonusRate * 100).toFixed(0)}</span>
        </div>
        {data.nextTier && (
          <p className="text-xs text-navy/60 mt-3 pt-3 border-t border-navy/10" data-testid="referral-next-tier">
            {data.invitesToNextTier} davet daha getir, primin <span className="text-gold font-medium">%{(data.nextTierRate * 100).toFixed(0)}</span>'e yükselsin.
          </p>
        )}
        {!data.nextTier && data.referralCount > 0 && (
          <p className="text-xs text-greenx mt-3 pt-3 border-t border-navy/10">En yüksek davet primi oranındasın.</p>
        )}
      </Card>

      {/* Referred-by / enter code */}
      {data.hasReferrer ? (
        <div className="flex items-center gap-2.5 bg-cream2 border border-navy/10 rounded-lg p-4 mb-4" data-testid="invited-by">
          <UserCheck strokeWidth={1.25} className="w-5 h-5 text-gold" />
          <p className="text-sm text-navy">Seni <span className="font-medium">{data.referrerName}</span> davet etti.</p>
        </div>
      ) : (
        <Card className="p-5 mb-4" data-testid="apply-invite-box">
          <div className="flex items-center gap-2 mb-3">
            <Ticket strokeWidth={1.25} className="w-4.5 h-4.5 text-gold" />
            <p className="text-sm font-medium text-navy">Davet kodun var mı?</p>
          </div>
          <div className="flex gap-2">
            <input
              data-testid="apply-invite-input"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              placeholder="MPXXXXXX"
              className="flex-1 bg-cream border border-navy/15 rounded-md px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-gold tracking-wide"
            />
            <button onClick={applyCode} disabled={applying} data-testid="apply-invite-btn" className="px-4 bg-navy text-cream rounded-md text-sm active:scale-95 transition-transform disabled:opacity-50">
              {applying ? '...' : 'Uygula'}
            </button>
          </div>
        </Card>
      )}

      {/* Club card */}
      <Card className="p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium text-navy">Kulüp Kartı</p>
          <span className="text-xs text-navy/50">{data.clubStampsForBonus} damga = {formatTL(data.clubBonus)}</span>
        </div>
        <div className="flex gap-2" data-testid="club-card">
          {Array.from({ length: data.clubStampsForBonus }).map((_, i) => (
            <div key={i} className={`flex-1 aspect-square rounded-md border flex items-center justify-center ${i < stampsInCycle ? 'bg-gold/15 border-gold/40' : 'border-navy/15 bg-cream'}`}>
              <Stamp strokeWidth={1.25} className={`w-5 h-5 ${i < stampsInCycle ? 'text-gold' : 'text-navy/20'}`} />
            </div>
          ))}
        </div>
        <p className="text-xs text-navy/50 mt-3">{stampsInCycle}/{data.clubStampsForBonus} damga · Her satış 1 damga</p>
        <div className="flex gap-3 mt-4 pt-4 border-t border-navy/10">
          <div className="flex-1">
            <p className="font-serif text-xl text-navy" data-testid="club-completed">{data.completedCards}</p>
            <p className="text-xs text-navy/50">Tamamlanan kart</p>
          </div>
          <div className="flex-1 text-right">
            <p className="font-serif text-xl text-greenx" data-testid="club-total-bonus">{formatTL(data.clubTotalBonus)}</p>
            <p className="text-xs text-navy/50">Kazanılan bonus</p>
          </div>
        </div>
      </Card>

      {/* Leaderboard */}
      {board && board.leaderboard.length > 0 && (
        <>
          <SectionTitle>Davet Şampiyonları</SectionTitle>
          <Card className="p-2 mb-5" data-testid="referral-leaderboard">
            {board.leaderboard.map((r) => (
              <div
                key={r.architectId}
                className={`flex items-center justify-between px-3 py-2.5 rounded-md ${r.isMe ? 'bg-gold/10' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${r.rank <= 3 ? 'text-cream' : 'text-navy/60 bg-cream'}`}
                    style={r.rank <= 3 ? { backgroundColor: ['#A9793D', '#9CA3AF', '#CD7F32'][r.rank - 1] } : {}}>
                    {r.rank}
                  </span>
                  <div>
                    <p className="text-sm text-navy font-medium flex items-center gap-1.5">
                      {r.name}
                      {r.isMe && <span className="text-[10px] text-gold">(Sen)</span>}
                    </p>
                    <p className="text-xs text-navy/50">{r.count} davet</p>
                  </div>
                </div>
                <span className="text-sm text-gold font-medium">{formatTL(r.bonus)}</span>
              </div>
            ))}
            {board.me && board.me.rank > 10 && (
              <div className="flex items-center justify-between px-3 py-2.5 rounded-md bg-gold/10 border-t border-navy/10 mt-1">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-navy/60 bg-cream">{board.me.rank}</span>
                  <p className="text-sm text-navy font-medium">{board.me.name} <span className="text-[10px] text-gold">(Sen)</span></p>
                </div>
                <span className="text-sm text-gold font-medium">{formatTL(board.me.bonus)}</span>
              </div>
            )}
          </Card>
        </>
      )}

      {/* Referred list */}
      <SectionTitle>Davetlerin</SectionTitle>
      {data.referrals.length === 0 ? (
        <Empty text="Henüz kimseyi davet etmedin" />
      ) : (
        <div className="space-y-2" data-testid="referral-list">
          {data.referrals.map((r, i) => (
            <Card key={i} className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-navy font-medium">{r.name}</p>
                <p className="text-xs text-navy/50 flex items-center gap-1.5">
                  {r.city} · <span className={r.countedSales > 0 ? 'text-greenx' : 'text-navy/50'}>{r.activityLabel}</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gold font-medium">+{formatTL(r.earnedBonus)}</p>
                <p className="text-xs text-navy/40">Davet primi</p>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
